import { Box, ThemeProvider, Typography, useTheme } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { ActivatedSwitch } from 'app/shared/components/activated-switch/ActivatedSwitch';
import { BoxForm, FormControlDefault, MenuItemStyled, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { RegisterTitle } from 'app/shared/layout/register/RegisterTitle';
import { TPMS_GROUP_TYPE_PRESSURE, TPMS_GROUP_TYPE_TEMPERATURE } from 'app/shared/model/alert.model';
import { AlertCluster } from 'app/shared/model/enumerations/alert-cluster.model';
import { AlertOperator } from 'app/shared/model/enumerations/alert-operator.model';
import { AlertType } from 'app/shared/model/enumerations/alert-type.model';
import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import SelectPaginate from '../../shared/components/select-paginate/SelectPaginate';
import { getEntityCluster, reset as resetSensor } from '../sensor/sensor.reducer';
import { createEntity, getAlertGroups, getEntity, reset, updateAlertGroups, updateEntity } from './alert.reducer';
import { TruckGroupSelectionModal } from './components/TruckGroupSelectionModal';

const TPMS_PRESSURE_LABEL    = 'Pressão Pneu (TPMS)';
const TPMS_TEMPERATURE_LABEL = 'Temperatura Pneu (TPMS)';

export const AlertUpdate = (props: RouteComponentProps<{ id: string }>) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { entity, loading, updateSuccess } = useAppSelector(state => state.alert);
  const { entitiesAlt2, loading: loadingCluster } = useAppSelector(state => state.sensor);
  const alertGroups: number[] = useAppSelector(state => state.alert.entitiesAlt2) ?? [];
  const clusterName = entitiesAlt2;

  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [customInput, setCustomInput] = useState(null as any);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    alertType: null,
    sensor: null,
    alertOperator: null,
    alertCluster: null,
    value: null,
    valueInRangeMin: null,
    valueInRangeMax: null,
    company: null,
    activated: null,
  });

  const alertTypes: AlertType[] = [AlertType.LIGHT, AlertType.MEDIUM, AlertType.SERIOUS, AlertType.CRITICAL];
  const inRangeOperator: AlertOperator = AlertOperator.IN_RANGE;
  const changedOperator: AlertOperator = AlertOperator.CHANGED;
  const alertClustersList: AlertCluster[] = [AlertCluster.TPMS, AlertCluster.CAN, AlertCluster.EBS, AlertCluster.DEFAULT, AlertCluster.OTHERS];
  const alertOperators: AlertOperator[] = [
    AlertOperator.EQUAL,
    AlertOperator.GREATER_THAN,
    AlertOperator.LESS_THAN,
    AlertOperator.IN_RANGE,
    AlertOperator.CHANGED,
  ];
  const [checked, setChecked] = useState(true);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [openGroupModal, setOpenGroupModal] = useState(false);

  const valueInRangeMinNameForms = 'valueInRangeMin';
  const valueInRangeMaxNameForms = 'valueInRangeMax';
  const valueNameForms = 'value';
  const isAllAdmins = isAdmin || isManufacturerAdmin || isTransporterAdmin || isRepresentativeAdmin;
  const isInRangeOperator = () => customInput === inRangeOperator;
  const isChangedOperator = () => customInput === changedOperator;

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }
    return () => {
      dispatch(resetSensor());
    };
  }, []);
  useEffect(() => {
    if (!isNew && entity) {
      setFormData({
        id: entity?.id,
        name: entity?.name,
        alertType: entity?.alertType,
        sensor: entity?.sensor,
        alertOperator: entity?.alertOperator,
        alertCluster: entity?.alertCluster,
        value: entity?.value,
        valueInRangeMin: entity?.valueInRangeMin,
        valueInRangeMax: entity?.valueInRangeMax,
        company: entity?.company,
        activated: entity?.activated,
      });
      setChecked(entity?.activated ?? true);
      if (entity?.id) {
        dispatch(getAlertGroups(entity.id));
      }
    }
  }, [entity]);

  useEffect(() => {
    setSelectedGroupIds(alertGroups);
  }, [JSON.stringify(alertGroups)]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (clusterName && !loadingCluster) {
      const isValidOption = alertClustersList.some(option => option === clusterName);
      setFormData(prevState => ({
        ...prevState,
        alertCluster: isValidOption ? clusterName : null,
      }));
    }
  }, [clusterName, loadingCluster]);

  useEffect(() => {
    setCustomInput(formData.alertOperator);
  }, [formData.alertOperator]);

  const handleSensorChange = sensor => {
    setFormData(prevState => ({
      ...prevState,
      sensor,
    }));
  };

  useEffect(() => {
    if (!formData.sensor?.id) {
      dispatch(resetSensor());
      return;
    }
    dispatch(getEntityCluster(formData.sensor.id));
  }, [formData.sensor?.id]);

  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  const saveEntity = async values => {
    const sensorName = values.sensor?.name;
    const tpmsGroupType =
      sensorName === TPMS_PRESSURE_LABEL    ? TPMS_GROUP_TYPE_PRESSURE :
      sensorName === TPMS_TEMPERATURE_LABEL ? TPMS_GROUP_TYPE_TEMPERATURE :
      null;

    const newEntity = {
      ...entity,
      ...values,
      sensor: tpmsGroupType ? null : values.sensor,
      tpmsGroupType,
      value: isInRangeOperator() || isChangedOperator() ? null : values.value,
      valueInRangeMax: isInRangeOperator() ? values.valueInRangeMax : null,
      valueInRangeMin: isInRangeOperator() ? values.valueInRangeMin : null,
      alertCluster: values.alertCluster,
    };

    if (!tpmsGroupType && !newEntity?.sensor?.id) {
      return toast.error('Necessário selecionar um sensor.');
    }

    let savedAction;
    if (isNew) {
      savedAction = await dispatch(createEntity(newEntity));
    } else {
      savedAction = await dispatch(updateEntity(newEntity));
    }

    const alertId = savedAction?.payload?.data?.id || entity?.id;
    if (alertId && selectedGroupIds !== null) {
      await dispatch(updateAlertGroups({ id: alertId, groupIds: selectedGroupIds }));
    }
  };

  const handleSubmit = async () => {
    event.preventDefault();
    await saveEntity(formData);
  };

  const handleClose = () => {
    props.history.push('/alert' + props.location.search);
  };

  return (
    <div>
      <ThemeProvider theme={theme}>
        <RegisterLayout>
          <Box sx={{ width: { xs: '95%', sm: '70%', lg: '50%' } }} justifyContent={'center'}>
            <RegisterTitle isNew={isNew} contentKey={'fleetSenseApp.alert'} />
            <Box>
              {loading ? (
                <Box>
                  <Typography>Loading...</Typography>
                </Box>
              ) : (
                <Box margin={'0 auto'} width={'100%'}>
                  <BoxForm onSubmit={handleSubmit}>
                    {!isNew && (
                      <FormControlDefault sx={{ display: 'none' }}>
                        <TypographyForms>{translate('global.field.id')}</TypographyForms>
                        <TextFieldForms value={formData['id']} id={'alert-activation-id'} name={'id'} />
                      </FormControlDefault>
                    )}
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.alert.name')}</TypographyForms>
                      <TextFieldForms
                        value={formData['name']}
                        id={'alert-name'}
                        data-cy={'name'}
                        name={'name'}
                        required={true}
                        onChange={e => handleInputChange('name', e.target.value)}
                      />
                    </FormControlDefault>
                    <FormControlDefault>
                      <TypographyForms>{translate('fleetSenseApp.alert.alertType')}</TypographyForms>
                      <TextFieldForms
                        value={formData['alertType'] || ''}
                        onChange={e => handleInputChange('alertType', e.target.value)}
                        select
                        required={true}
                        id={'alert-alertType'}
                        name={'alertType'}
                      >
                        {alertTypes.map(option => (
                          <MenuItemStyled value={option} key={option}>
                            {translate(`fleetSenseApp.AlertType.${option}`)}
                          </MenuItemStyled>
                        ))}
                      </TextFieldForms>
                    </FormControlDefault>
                    <FormControlDefault>
                      <SelectPaginate
                        label={translate('fleetSenseApp.alert.sensor')}
                        apiUrl={'api/allSensorsListWithPagination'}
                        onChange={handleSensorChange}
                        value={formData['sensor']}
                        valueName="name"
                        icon="sort-down"
                        isRequired={true}
                        statusFilter
                        filterStyle={true}
                      />
                      {(formData['sensor']?.name === TPMS_PRESSURE_LABEL || formData['sensor']?.name === TPMS_TEMPERATURE_LABEL) && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Este alerta será aplicado a todos os sensores do grupo automaticamente.
                        </Typography>
                      )}
                    </FormControlDefault>
                    <FormControlDefault>
                      <TypographyForms>{translate('fleetSenseApp.alert.alertCluster')}</TypographyForms>
                      <TextFieldForms
                        value={formData['alertCluster'] || ''}
                        onChange={e => handleInputChange('alertCluster', e.target.value)}
                        data-cy={'alertCluster'}
                        select
                        required={true}
                        id={'alert-alertCluster'}
                        name={'alertCluster'}
                        // disabled={loadingCluster}
                        disabled
                      >
                        {loadingCluster && <MenuItemStyled disabled>Buscando cluster...</MenuItemStyled>}
                        {alertClustersList.map(option => (
                          <MenuItemStyled value={option} key={option}>
                            {translate(`fleetSenseApp.AlertCluster.${option}`)}
                          </MenuItemStyled>
                        ))}
                      </TextFieldForms>
                    </FormControlDefault>
                    <FormControlDefault>
                      <TypographyForms>{translate('fleetSenseApp.alert.alertOperator')}</TypographyForms>
                      <TextFieldForms
                        value={formData['alertOperator'] || ''}
                        onChange={e => handleInputChange('alertOperator', e.target.value)}
                        data-cy={'alertOperator'}
                        select
                        required={true}
                        id={'alert-alertOperator'}
                        name={'alertOperator'}
                      >
                        {alertOperators.map(option => (
                          <MenuItemStyled value={option} key={option}>
                            {translate(`fleetSenseApp.AlertOperator.${option}`)}
                          </MenuItemStyled>
                        ))}
                      </TextFieldForms>
                    </FormControlDefault>
                    {!isInRangeOperator() && !isChangedOperator() && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.alert.value')}</TypographyForms>
                        <TextFieldForms
                          value={formData['value']}
                          id={'alert-value'}
                          name={valueNameForms}
                          data-cy={'value'}
                          required={true}
                          onChange={e => handleInputChange('value', e.target.value)}
                          type="number"
                        />
                      </FormControlDefault>
                    )}
                    {isInRangeOperator() && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.alert.valueInRangeMin')}</TypographyForms>
                        <TextFieldForms
                          value={formData['valueInRangeMin']}
                          id={'alert-valueInRangeMin'}
                          name={valueInRangeMinNameForms}
                          data-cy={'valueInRangeMin'}
                          required={true}
                          onChange={e => handleInputChange('valueInRangeMin', e.target.value)}
                          type="number"
                        />
                      </FormControlDefault>
                    )}
                    {isInRangeOperator() && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.alert.valueInRangeMax')}</TypographyForms>
                        <TextFieldForms
                          value={formData['valueInRangeMax']}
                          id={'alert-valueInRangeMax'}
                          name={valueInRangeMaxNameForms}
                          data-cy={'valueInRangeMax'}
                          required={true}
                          onChange={e => handleInputChange('valueInRangeMax', e.target.value)}
                          type="number"
                        />
                      </FormControlDefault>
                    )}
                    {isAdmin && (
                      <FormControlDefault>
                        <SelectPaginate
                          label={translate('fleetSenseApp.userCustom.company')}
                          apiUrl={'api/companies'}
                          onChange={value => handleInputChange('company', value)}
                          value={formData['company']}
                          valueName="name"
                          icon="sort-down"
                          isRequired={true}
                          filterStyle={true}
                        />
                      </FormControlDefault>
                    )}
                    <FormControlDefault variant="outlined">
                      <TypographyForms>Grupos de Ativos</TypographyForms>
                      <TextFieldForms
                        onClick={() => (!isAdmin || formData['company']?.id) && setOpenGroupModal(true)}
                        value={
                          isAdmin && !formData['company']?.id
                            ? 'Selecione uma empresa para ver os grupos'
                            : selectedGroupIds.length > 0
                            ? `${selectedGroupIds.length} grupo${selectedGroupIds.length !== 1 ? 's' : ''} associado${selectedGroupIds.length !== 1 ? 's' : ''}`
                            : 'Clique para associar grupos'
                        }
                        InputProps={{ readOnly: true }}
                        disabled={isAdmin && !formData['company']?.id}
                        sx={{ cursor: isAdmin && !formData['company']?.id ? 'not-allowed' : 'pointer' }}
                      />
                    </FormControlDefault>
                    {isAllAdmins && <ActivatedSwitch checked={checked} setChecked={setChecked} handleInputChange={handleInputChange} />}
                    <RegisterButton />
                  </BoxForm>
                </Box>
              )}
            </Box>
          </Box>
        </RegisterLayout>
      </ThemeProvider>
      {openGroupModal && (
        <TruckGroupSelectionModal
          open={openGroupModal}
          toggleModal={() => setOpenGroupModal(false)}
          initialSelectedGroupIds={selectedGroupIds}
          handleSave={ids => setSelectedGroupIds(ids)}
          companyId={formData['company']?.id}
        />
      )}
    </div>
  );
};
